import React from 'react';
import { Icon } from '@iconify-icon/react';
import { Button } from '@mantine/core';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

const commitments = [
  'Zero data retention by default for AWS Bedrock inference',
  'No model-provider training on Bedrock inputs or outputs',
  'Encryption at rest and in transit',
  'Role-based access controls and authenticated private agents',
  'Security logging for operational oversight',
  'Export or permanently delete your data at any time',
];

const PrivacyCommitments: React.FC = () => (
  <section aria-label="Our privacy commitments" className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
        {/* Left: the promise */}
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b8862e]">
            Enterprise assurance
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Clear safeguards,{' '}
            <span className="font-display font-medium italic text-[#004790]">stated plainly.</span>
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-gray-500">
            The privacy story should be understandable before legal review. These safeguards apply
            from the moment you start, including during your 14-day free trial.
          </p>
          <ul className="mt-8 space-y-3.5">
            {commitments.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                  <Icon icon="tabler:check" className="text-sm text-emerald-600" />
                </span>
                <span className="text-[15px] font-medium leading-relaxed text-gray-800">
                  {item}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#004790] shadow-sm">
              <Icon icon="tabler:certificate" className="text-xl" />
            </span>
            <div>
              <p className="text-sm font-bold text-gray-900">ISO/IEC 27001:2022 certified</p>
              <p className="mt-1 text-xs leading-relaxed text-gray-600">
                SA CYBER LLC&apos;s information security management system supporting Chat.co is
                certified by GCAI. Certificate GCAI-ISMS-LM73 is valid through August 28, 2029.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <Link
                  href="/certifications/sa-cyber-iso-27001-2022.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#004790] underline underline-offset-2 hover:text-primary-700">
                  View certificate
                </Link>
                <span className="text-gray-500">SOC 2 Type II audit in progress</span>
              </div>
            </div>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-gray-400">
            The fine print matters too: read the full{' '}
            <Link
              href="/privacy-policy"
              className="underline underline-offset-2 hover:text-gray-600">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              href="/terms-condition"
              className="underline underline-offset-2 hover:text-gray-600">
              Terms of Service
            </Link>
            . Statements about zero data retention and model-provider training apply when Chat.co
            uses AWS Bedrock.
          </p>
        </div>

        {/* Right: CTA card */}
        <div className="relative overflow-hidden rounded-[28px] bg-[#04182e] p-8 sm:p-12">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff07_1px,transparent_1px),linear-gradient(to_bottom,#ffffff07_1px,transparent_1px)] bg-[size:3rem_3rem]" />
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-600/40 blur-[80px]" />
          </div>
          <div className="relative">
            <Icon icon="tabler:shield-lock" className="text-4xl text-[#d3a641]" />
            <h3 className="mt-5 font-display text-2xl font-medium italic leading-snug text-white sm:text-3xl">
              Move faster with AI.
              <br />
              Keep control of your knowledge.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-blue-200/70">
              Chat.co gives teams the speed of generative AI without surrendering control of their
              knowledge, access policies, or customer experience.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                component={Link}
                href={PageLink.login}
                className="hero-cta-btn rounded-xl bg-primary-600 px-8 text-base font-semibold shadow-lg shadow-blue-900/40">
                Start 14-Day Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/contact-us"
                classNames={{
                  root: 'rounded-xl border-white/25 px-8 text-base font-semibold text-white hover:bg-white/10',
                }}>
                Talk to us
              </Button>
            </div>
            <p className="mt-4 text-xs text-blue-200/50">No credit card required</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default PrivacyCommitments;
